"use client";

import type {
  AssistantStaticBubbleVariantProps,
  UserStaticBubbleVariantProps,
} from "@ui/components/core/BaseChat/styles";
import type {
  ChatPlayerCompiledChat,
  ChatPlayerMessage,
  ChatPlayerPart,
  ChatPlayerPartSnapshot,
  ChatPlayerRole,
  ChatPlayerScenarioSource,
  ChatPlayerTranscriptSource,
  ChatPlayerTypingTimelineEvent,
} from "./chat-player-types";
import type { ChatPlayerPlayback } from "./chat-player-playback-driver";
import type { ReactNode } from "react";

import { TypingDots } from "@ui/components/core/BaseChat";
import { MessagesList } from "@ui/components/core/BaseChat/BaseChat";
import {
  assistantBubbleBackgroundVariants,
  assistantBubbleRootVariants,
  assistantStaticBubbleVariants,
  userStaticBubbleVariants,
} from "@ui/components/core/BaseChat/styles";
import { cn } from "@ui/lib/utils";
import { clsx } from "clsx";
import { AnimatePresence, LayoutGroup, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import { forwardRef, memo, useEffect, useMemo } from "react";

import { ChatPlayerPlaybackDriver, getInitialChatPlayerPlaybackTimeMs } from "./chat-player-playback-driver";
import {
  ChatPlayerPlaybackProvider,
  useChatPlayerForegroundPartKeys,
  useChatPlayerPlaybackKey,
  useChatPlayerSpeechVisibleWordIndex,
  useChatPlayerToolStatus,
  useChatPlayerVisibleMessages,
  useChatPlayerVisibleParts,
  useChatPlayerVisibleTypingEvents,
} from "./chat-player-playback-store";
import {
  getChatPlayerPartKey,
  getChatPlayerSpeechPartSnapshot,
  getChatPlayerToolPartSnapshot,
} from "./chat-player-playback";
import { ChatPlayerPartContent, type ChatPlayerPartRegistry } from "./chat-player-parts";
import { compileChatPlayerScenario } from "./chat-player-scenario";
import { compileChatPlayerTranscript } from "./chat-player-transcript";

export type { ChatPlayerPlayback } from "./chat-player-playback-driver";
export type { ChatPlayerPartRegistry } from "./chat-player-parts";

export type ChatPlayerSource = ChatPlayerScenarioSource | ChatPlayerTranscriptSource;

export type ChatPlayerProps = {
  source: ChatPlayerSource;
  anchor?: "top" | "bottom";
  animateInitialSnapshot?: boolean;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
  className?: string;
  contentClassName?: string;
  maxVisibleBubbles?: number;
  onDurationChange?: (durationMs: number) => void;
  partRegistry?: ChatPlayerPartRegistry;
  playback?: ChatPlayerPlayback;
  userMessageVariants?: UserStaticBubbleVariantProps;
};

type CompiledChatPlayerViewProps = {
  chat: ChatPlayerCompiledChat;
  playback: ChatPlayerPlayback;
  anchor: "top" | "bottom";
  animateInitialSnapshot: boolean;
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  className?: string;
  contentClassName?: string;
  maxVisibleBubbles?: number;
  partRegistry?: ChatPlayerPartRegistry;
  userMessageVariants: UserStaticBubbleVariantProps;
};

type ChatPlayerVisibleRow =
  | { type: "message"; id: string; message: ChatPlayerMessage }
  | { type: "typing"; id: string; event: ChatPlayerTypingTimelineEvent };

type ChatPlayerMessageRowProps = {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  row: ChatPlayerVisibleRow;
  partRegistry?: ChatPlayerPartRegistry;
  foregroundPartKeySet: ReadonlySet<string> | null;
  reducedMotion: boolean;
  userMessageVariants: UserStaticBubbleVariantProps;
};

type ChatPlayerPartBubbleProps = {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  messageId: string;
  part: ChatPlayerPart;
  partRegistry?: ChatPlayerPartRegistry;
  isFaded: boolean;
  reducedMotion: boolean;
  role: ChatPlayerRole;
  userMessageVariants: UserStaticBubbleVariantProps;
};

type ChatPlayerBubbleFrameProps = {
  backgroundClassName?: string;
  children: ReactNode;
  isFaded?: boolean;
  layoutMode?: "full" | "none" | "position";
  origin: ChatPlayerRole | "tool";
  reducedMotion: boolean;
  rootClassName: string;
};

const DEFAULT_PLAYBACK = { type: "auto", active: true } satisfies ChatPlayerPlayback;
const BUBBLE_ENTER_SCALE = 0.3;
const BUBBLE_ENTER_ROTATE_DEGREES = 1;
const BUBBLE_ENTER_TRANSITION = { duration: 0.7, ease: [0.19, 1, 0.22, 1] } as const;
const BUBBLE_FADE_TRANSITION = { duration: 0.56, ease: [0.22, 1, 0.36, 1] } as const;
export const BUBBLE_LAYOUT_TRANSITION = { duration: 0.42, ease: [0.22, 1, 0.36, 1] } as const;
// Keep entering bubbles barely painted so Motion can warm up backdrop-filter state before the first visible frame.
const BUBBLE_ENTER_INITIAL_OPACITY = 0.001;
const MESSAGE_ENTER_TRANSITION = {
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1],
  layout: BUBBLE_LAYOUT_TRANSITION,
} as const;
const TYPING_ROW_EXIT_TRANSITION = { duration: 0.18, ease: [0.25, 0.1, 0.25, 1] } as const;
const MESSAGE_PARTS_CLASS_NAME = "tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-flex-col tw-gap-2";
const TOOL_BUBBLE_VARIANTS = { rounded: "pill", tail: "none" } satisfies AssistantStaticBubbleVariantProps;
const MESSAGE_BUBBLE_RAIL_CLASS_NAMES = {
  assistant: "tw-mr-auto tw-flex tw-w-full tw-min-w-0 tw-max-w-[90%] tw-justify-start",
  user: "tw-ml-auto tw-flex tw-w-full tw-min-w-0 tw-max-w-[90%] tw-justify-end",
} satisfies Record<ChatPlayerRole, string>;
const MESSAGE_PARTS_ALIGNMENT_CLASS_NAMES = {
  assistant: "tw-items-start",
  user: "tw-items-end",
} satisfies Record<ChatPlayerRole, string>;
const BUBBLE_LAYOUT_ORIGINS = {
  assistant: { originX: 0, originY: 1, transformOrigin: "bottom left" },
  tool: { originX: 0, originY: 1, transformOrigin: "bottom left" },
  user: { originX: 1, originY: 1, transformOrigin: "bottom right" },
} satisfies Record<ChatPlayerRole | "tool", { originX: number; originY: number; transformOrigin: string }>;
const defaultAssistantMessageVariants = { variant: "cream" } satisfies AssistantStaticBubbleVariantProps;
const defaultUserMessageVariants = { variant: "elevated" } satisfies UserStaticBubbleVariantProps;

export function ChatPlayer({
  anchor = "bottom",
  animateInitialSnapshot = false,
  assistantMessageVariants = defaultAssistantMessageVariants,
  className,
  contentClassName,
  maxVisibleBubbles,
  onDurationChange,
  partRegistry,
  playback = DEFAULT_PLAYBACK,
  source,
  userMessageVariants = defaultUserMessageVariants,
}: ChatPlayerProps) {
  const chat = useMemo(() => compileChatPlayerSource(source), [source]);

  useEffect(() => {
    onDurationChange?.(chat.durationMs);
  }, [chat.durationMs, onDurationChange]);

  return (
    <CompiledChatPlayerView
      key={chat.scopeKey}
      anchor={anchor}
      animateInitialSnapshot={animateInitialSnapshot}
      assistantMessageVariants={assistantMessageVariants}
      chat={chat}
      className={className}
      contentClassName={contentClassName}
      maxVisibleBubbles={maxVisibleBubbles}
      partRegistry={partRegistry}
      playback={playback}
      userMessageVariants={userMessageVariants}
    />
  );
}

function compileChatPlayerSource(source: ChatPlayerSource) {
  return "segments" in source ? compileChatPlayerTranscript(source) : compileChatPlayerScenario(source);
}

function CompiledChatPlayerView({
  anchor,
  animateInitialSnapshot,
  assistantMessageVariants,
  chat,
  className,
  contentClassName,
  maxVisibleBubbles,
  partRegistry,
  playback,
  userMessageVariants,
}: CompiledChatPlayerViewProps) {
  const initialTimeMs = getInitialChatPlayerPlaybackTimeMs(playback, chat.durationMs);

  return (
    <ChatPlayerPlaybackProvider chat={chat} initialTimeMs={initialTimeMs}>
      <ChatPlayerPlaybackDriver chat={chat} playback={playback} />
      <ChatPlayerMessagesView
        anchor={anchor}
        animateInitialSnapshot={animateInitialSnapshot}
        assistantMessageVariants={assistantMessageVariants}
        chat={chat}
        className={className}
        contentClassName={contentClassName}
        maxVisibleBubbles={maxVisibleBubbles}
        partRegistry={partRegistry}
        userMessageVariants={userMessageVariants}
      />
    </ChatPlayerPlaybackProvider>
  );
}

function ChatPlayerMessagesView({
  anchor,
  animateInitialSnapshot,
  assistantMessageVariants,
  chat,
  className,
  contentClassName,
  maxVisibleBubbles,
  partRegistry,
  userMessageVariants,
}: Omit<CompiledChatPlayerViewProps, "playback">) {
  const prefersReducedMotion = useReducedMotion();
  const reducedMotion = Boolean(prefersReducedMotion);
  const playbackKey = useChatPlayerPlaybackKey();
  const foregroundPartKeys = useChatPlayerForegroundPartKeys(chat, maxVisibleBubbles);
  const foregroundPartKeySet = useMemo(
    () => (foregroundPartKeys ? new Set(foregroundPartKeys) : null),
    [foregroundPartKeys],
  );
  const visibleMessages = useChatPlayerVisibleMessages(chat);
  const visibleTypingEvents = useChatPlayerVisibleTypingEvents();
  const visibleRows = useMemo(
    () => getChatPlayerVisibleRows(chat.messages, visibleMessages, visibleTypingEvents),
    [chat.messages, visibleMessages, visibleTypingEvents],
  );
  const playbackScopeKey = `${chat.scopeKey}-${playbackKey}`;

  return (
    <MessagesList
      anchor={anchor}
      className={className}
      contentClassName={cn("tw-overflow-x-hidden tw-overflow-y-hidden tw-scrollbar-none tw-p-1.5", contentClassName)}
    >
      <LayoutGroup id={playbackScopeKey}>
        <AnimatePresence initial={!reducedMotion && animateInitialSnapshot} mode='popLayout'>
          {visibleRows.map(row => (
            <ChatPlayerMessageRow
              key={`${playbackScopeKey}-${row.type}-${row.id}`}
              assistantMessageVariants={assistantMessageVariants}
              foregroundPartKeySet={foregroundPartKeySet}
              partRegistry={partRegistry}
              reducedMotion={reducedMotion}
              row={row}
              userMessageVariants={userMessageVariants}
            />
          ))}
        </AnimatePresence>
      </LayoutGroup>
    </MessagesList>
  );
}

function getChatPlayerVisibleRows(
  messages: ChatPlayerMessage[],
  visibleMessages: ChatPlayerMessage[],
  visibleTypingEvents: ChatPlayerTypingTimelineEvent[],
) {
  const visibleMessageById = new Map(visibleMessages.map(message => [message.id, message]));
  const visibleTypingByMessageId = new Map(visibleTypingEvents.map(event => [event.messageId, event]));
  const rows: ChatPlayerVisibleRow[] = [];

  messages.forEach(message => {
    const visibleMessage = visibleMessageById.get(message.id);
    const typingEvent = visibleTypingByMessageId.get(message.id);

    if (visibleMessage) {
      rows.push({ type: "message", id: message.id, message: visibleMessage });
      return;
    }

    if (typingEvent) {
      rows.push({ type: "typing", id: message.id, event: typingEvent });
    }
  });

  return rows;
}

const ChatPlayerMessageRow = memo(
  forwardRef<HTMLElement, ChatPlayerMessageRowProps>(function ChatPlayerMessageRow(
    { assistantMessageVariants, foregroundPartKeySet, partRegistry, reducedMotion, row, userMessageVariants },
    ref,
  ) {
    const role = row.type === "message" ? row.message.role : row.event.role;

    return (
      <m.article
        ref={ref}
        className='tw-flex tw-w-full'
        style={row.type === "typing" ? BUBBLE_LAYOUT_ORIGINS[role] : undefined}
        {...getMessageMotionProps(reducedMotion, row.type)}
      >
        <div className={MESSAGE_BUBBLE_RAIL_CLASS_NAMES[role]}>
          {row.type === "typing" ? (
            <div className={clsx(MESSAGE_PARTS_CLASS_NAME, MESSAGE_PARTS_ALIGNMENT_CLASS_NAMES[role])}>
              <ChatPlayerTypingBubble
                assistantMessageVariants={assistantMessageVariants}
                event={row.event}
                reducedMotion={reducedMotion}
                userMessageVariants={userMessageVariants}
              />
            </div>
          ) : (
            <ChatPlayerMessageParts
              assistantMessageVariants={assistantMessageVariants}
              foregroundPartKeySet={foregroundPartKeySet}
              message={row.message}
              partRegistry={partRegistry}
              reducedMotion={reducedMotion}
              userMessageVariants={userMessageVariants}
            />
          )}
        </div>
      </m.article>
    );
  }),
);

function ChatPlayerTypingBubble({
  assistantMessageVariants,
  event,
  reducedMotion,
  userMessageVariants,
}: {
  assistantMessageVariants: AssistantStaticBubbleVariantProps;
  event: ChatPlayerTypingTimelineEvent;
  reducedMotion: boolean;
  userMessageVariants: UserStaticBubbleVariantProps;
}) {
  return (
    <ChatPlayerBubbleFrame
      {...getBubbleFrameClassNames(event.role, assistantMessageVariants, userMessageVariants)}
      origin={event.role}
      reducedMotion={reducedMotion}
    >
      <TypingDots />
    </ChatPlayerBubbleFrame>
  );
}

function ChatPlayerMessageParts({
  assistantMessageVariants,
  foregroundPartKeySet,
  message,
  partRegistry,
  reducedMotion,
  userMessageVariants,
}: Omit<ChatPlayerMessageRowProps, "row"> & {
  message: ChatPlayerMessage;
}) {
  const parts = useChatPlayerVisibleParts(message);

  return (
    <div className={clsx(MESSAGE_PARTS_CLASS_NAME, MESSAGE_PARTS_ALIGNMENT_CLASS_NAMES[message.role])}>
      {parts.map(part => (
        <ChatPlayerPartBubble
          key={part.id}
          assistantMessageVariants={assistantMessageVariants}
          isFaded={foregroundPartKeySet ? !foregroundPartKeySet.has(getChatPlayerPartKey(message.id, part.id)) : false}
          messageId={message.id}
          part={part}
          partRegistry={partRegistry}
          reducedMotion={reducedMotion}
          role={message.role}
          userMessageVariants={userMessageVariants}
        />
      ))}
    </div>
  );
}

function getMessageMotionProps(reducedMotion: boolean, rowType: ChatPlayerVisibleRow["type"]) {
  if (reducedMotion) {
    return {
      layout: false as const,
      initial: false as const,
      transition: { duration: 0 },
    };
  }

  return {
    layout: "position" as const,
    initial: { y: 12 },
    animate: { y: 0 },
    exit:
      rowType === "typing"
        ? {
            opacity: 0,
            scale: 0.94,
            transition: TYPING_ROW_EXIT_TRANSITION,
            y: -6,
          }
        : { opacity: 0, y: -6 },
    transition: MESSAGE_ENTER_TRANSITION,
  };
}

function ChatPlayerPartBubble({
  assistantMessageVariants,
  isFaded,
  messageId,
  part,
  partRegistry,
  reducedMotion,
  role,
  userMessageVariants,
}: ChatPlayerPartBubbleProps) {
  switch (part.type) {
    case "speech":
      return (
        <ChatPlayerSpeechPartBubble
          assistantMessageVariants={assistantMessageVariants}
          isFaded={isFaded}
          messageId={messageId}
          part={part}
          partRegistry={partRegistry}
          reducedMotion={reducedMotion}
          role={role}
          userMessageVariants={userMessageVariants}
        />
      );
    case "tool":
      return (
        <ChatPlayerToolPartBubble
          assistantMessageVariants={assistantMessageVariants}
          isFaded={isFaded}
          messageId={messageId}
          part={part}
          partRegistry={partRegistry}
          reducedMotion={reducedMotion}
          role={role}
          userMessageVariants={userMessageVariants}
        />
      );
    case "text":
    case "custom":
      return (
        <ChatPlayerPartBubbleFrame
          assistantMessageVariants={assistantMessageVariants}
          isFaded={isFaded}
          part={part}
          partRegistry={partRegistry}
          reducedMotion={reducedMotion}
          role={role}
          userMessageVariants={userMessageVariants}
        />
      );
  }
}

function ChatPlayerSpeechPartBubble({
  assistantMessageVariants,
  isFaded,
  messageId,
  part,
  partRegistry,
  reducedMotion,
  role,
  userMessageVariants,
}: Omit<ChatPlayerPartBubbleProps, "messageId" | "part"> & {
  messageId: string;
  part: Extract<ChatPlayerPart, { type: "speech" }>;
}) {
  const visibleWordIndex = useChatPlayerSpeechVisibleWordIndex(messageId, part);
  const visiblePart = useMemo(() => getChatPlayerSpeechPartSnapshot(part, visibleWordIndex), [part, visibleWordIndex]);

  return (
    <ChatPlayerPartBubbleFrame
      assistantMessageVariants={assistantMessageVariants}
      isFaded={isFaded}
      part={visiblePart}
      partRegistry={partRegistry}
      reducedMotion={reducedMotion}
      role={role}
      userMessageVariants={userMessageVariants}
    />
  );
}

function ChatPlayerToolPartBubble({
  assistantMessageVariants,
  isFaded,
  messageId,
  part,
  partRegistry,
  reducedMotion,
  role,
  userMessageVariants,
}: Omit<ChatPlayerPartBubbleProps, "part"> & {
  part: Extract<ChatPlayerPart, { type: "tool" }>;
}) {
  const status = useChatPlayerToolStatus(messageId, part);
  const visiblePart = useMemo(() => getChatPlayerToolPartSnapshot(part, status), [part, status]);

  return (
    <ChatPlayerPartBubbleFrame
      assistantMessageVariants={assistantMessageVariants}
      isFaded={isFaded}
      part={visiblePart}
      partRegistry={partRegistry}
      reducedMotion={reducedMotion}
      role={role}
      userMessageVariants={userMessageVariants}
    />
  );
}

function ChatPlayerPartBubbleFrame({
  assistantMessageVariants,
  isFaded,
  part,
  partRegistry,
  reducedMotion,
  role,
  userMessageVariants,
}: Omit<ChatPlayerPartBubbleProps, "messageId" | "part"> & {
  part: ChatPlayerPartSnapshot;
}) {
  const bubbleOrigin = part.type === "tool" ? "tool" : role;
  const bubbleFrameClassNames = getBubbleFrameClassNames(
    role,
    assistantMessageVariants,
    userMessageVariants,
    part.type === "tool",
  );
  const layoutMode = bubbleFrameClassNames.layoutMode ?? (part.type === "tool" ? "position" : "full");

  return (
    <ChatPlayerBubbleFrame
      {...bubbleFrameClassNames}
      isFaded={isFaded}
      layoutMode={layoutMode}
      origin={bubbleOrigin}
      reducedMotion={reducedMotion}
    >
      <ChatPlayerPartContent part={part} partRegistry={partRegistry} reducedMotion={reducedMotion} role={role} />
    </ChatPlayerBubbleFrame>
  );
}

function ChatPlayerBubbleFrame({
  backgroundClassName,
  children,
  isFaded = false,
  layoutMode = "full",
  origin,
  reducedMotion,
  rootClassName,
}: ChatPlayerBubbleFrameProps) {
  const layoutOrigin = BUBBLE_LAYOUT_ORIGINS[origin];
  const layout = reducedMotion || layoutMode === "none" ? false : layoutMode === "position" ? "position" : true;

  return (
    <m.div
      className={clsx("tw-min-w-0", rootClassName)}
      layout={layout}
      style={layoutOrigin}
      {...getBubbleMotionProps(origin, reducedMotion, isFaded)}
    >
      {backgroundClassName ? <div aria-hidden className={backgroundClassName} /> : null}
      {children}
    </m.div>
  );
}

function getBubbleMotionProps(origin: ChatPlayerRole | "tool", reducedMotion: boolean, isFaded: boolean) {
  if (reducedMotion) {
    return {
      animate: isFaded ? { opacity: 0, visibility: "hidden" as const } : { opacity: 1, visibility: "visible" as const },
      initial: false as const,
      transition: { duration: 0, layout: { duration: 0 } },
    };
  }

  const initialRotate =
    origin === "tool" ? 0 : origin === "user" ? -BUBBLE_ENTER_ROTATE_DEGREES : BUBBLE_ENTER_ROTATE_DEGREES;

  return {
    initial: {
      opacity: BUBBLE_ENTER_INITIAL_OPACITY,
      rotate: initialRotate,
      scale: BUBBLE_ENTER_SCALE,
      visibility: "visible" as const,
      y: 0,
    },
    // Keep faded bubbles mounted for layout/backdrop continuity, then stop painting them after the fade.
    animate: isFaded
      ? {
          opacity: 0,
          rotate: 0,
          scale: 1,
          transitionEnd: { visibility: "hidden" as const },
          y: 0,
        }
      : {
          opacity: 1,
          rotate: 0,
          scale: 1,
          visibility: "visible" as const,
          y: 0,
        },
    transition: { ...(isFaded ? BUBBLE_FADE_TRANSITION : BUBBLE_ENTER_TRANSITION), layout: BUBBLE_LAYOUT_TRANSITION },
  };
}

function getBubbleFrameClassNames(
  role: ChatPlayerRole,
  assistantMessageVariants: AssistantStaticBubbleVariantProps,
  userMessageVariants: UserStaticBubbleVariantProps,
  isTool = false,
) {
  if (role === "user" && !isTool) {
    return {
      // User overlay/frosted variants rely on backdrop-filter; keep that on the animated shell to avoid first-frame blur flicker.
      layoutMode: "none" as const,
      rootClassName: userStaticBubbleVariants(userMessageVariants),
    };
  }

  const assistantVariants = isTool
    ? { ...assistantMessageVariants, ...TOOL_BUBBLE_VARIANTS }
    : assistantMessageVariants;

  if (assistantVariants.variant === "frostedGray") {
    return {
      // Frosted assistant bubbles need the same shell-level backdrop-filter path as the #3548 fix.
      layoutMode: "none" as const,
      rootClassName: assistantStaticBubbleVariants(assistantVariants),
    };
  }

  return {
    backgroundClassName: assistantBubbleBackgroundVariants(assistantVariants),
    rootClassName: assistantBubbleRootVariants(assistantVariants),
  };
}
